import momenttz from 'moment-timezone';

import { MARKET_DATA_DATE, MARKET_DATA_TIME_ZONE } from '../../constants/date-time.constants';
import { MarketCountryCode } from '../../models/fundamental/types';
import { GWMarketDataSummaryBucket } from '../../models/gateway/types';
import { MarketStateInstrumentSnapshot, OHLCV } from '../../models/market-data/types';
import MarketHoursState from '../../store-util/MarketHoursState';
import { CalculatedSymbolPrices } from '../../util/ChartingHelpers';
import { isDateEqual } from '../../util/DateTimeHelpers';
import { calculateMarketState } from '../../util/temporalDeadZoneFunctions';
import { NullableNumber } from '../../util/types';

import { SymbolPriceData } from './charting/types';
import { InstrumentSnapshotData, OHLCVDataForPriceCalculation } from './types';

// Call-time resolution left over from the Jest (CommonJS) runner, where it let a
// test's `doMock` win. Under Vitest this falls back to the static import.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const require: any;
function getMomentTzLocal() {
  try {
    const m = require('moment-timezone');
    return m?.default ?? m;
  } catch (_e) {
    return momenttz;
  }
}

export const EMPTY_PRICE_DATA: SymbolPriceData = {
  lastPrice: null,
  close: null,
  previousClose: null,
  afterClose: null,
  previousAfterClose: null,
  hasAfterMarketData: null,
  hasDataForToday: null,
};

export const EMPTY_CALCULATED_PRICE_DATA: CalculatedSymbolPrices = {
  lastTradePrice: null,
  lastTradeTime: null,
  currentMainPrice: null,
  prevMainPrice: null,
  hasAfterMarketData: null,
  mainPriceDirection: '',
  extraPriceDirection: '',
};

function getOHLCVItemDateAndTime(item: OHLCV) {
  const { dateAndTime, date, time } = item ?? {};
  return dateAndTime ?? `${date} ${time}`;
}

type CalculationInputType = 'instrument-snapshot' | 'chart-data';

/**
 * Close line of the big chart and the sparkline. During regular hours the snapshot's close is the
 * running last trade, so only prevClose counts there; once the session has closed it is today's
 * official close, which is what after-hours prices are compared against.
 */
export const getLastRegularSessionClose = (
  marketState: MarketStateInstrumentSnapshot | null,
  { close, prevClose }: Pick<InstrumentSnapshotData, 'close' | 'prevClose'>,
): NullableNumber => {
  const isRegularSessionRunning = marketState === MarketStateInstrumentSnapshot.IS_RegularHours;
  const sessionClose = isRegularSessionRunning ? null : close;

  return sessionClose || prevClose || null;
};

export function parseSymbolPriceSourceData(
  inputType: CalculationInputType,
  newData: OHLCVDataForPriceCalculation | InstrumentSnapshotData,
  prevData?: SymbolPriceData,
  countryCode?: string,
) {
  const { marketHours } = MarketHoursState.get(countryCode as MarketCountryCode);
  const timeZone = marketHours.timeZone || MARKET_DATA_TIME_ZONE;
  const result = { ...(prevData ?? EMPTY_PRICE_DATA) };
  const marketStateInstrumentSnapshot = calculateMarketState(undefined, countryCode);

  switch (inputType) {
    case 'instrument-snapshot': {
      const { prevClose, close, lastPrice, prevLastPrice } = newData as InstrumentSnapshotData;
      switch (marketStateInstrumentSnapshot) {
        case MarketStateInstrumentSnapshot.IS_PreMarket:
          result.lastPrice = lastPrice ?? prevLastPrice ?? prevClose;
          result.close = prevClose;
          result.afterClose = prevLastPrice;
          result.hasAfterMarketData = !!(prevLastPrice && lastPrice && prevLastPrice !== lastPrice);
          // NOTE: Instrument Snapshot is lacking this data - previousClose, previousAfterClose
          break;

        case MarketStateInstrumentSnapshot.IS_RegularHours:
          result.lastPrice = lastPrice ?? prevLastPrice ?? prevClose;
          result.close = prevClose;
          result.afterClose = prevLastPrice;
          result.hasAfterMarketData = !!(prevLastPrice && lastPrice && prevLastPrice !== lastPrice);
          // NOTE: Instrument Snapshot is lacking this data - previousClose, previousAfterClose
          break;

        case MarketStateInstrumentSnapshot.IS_AfterMarket:
          result.lastPrice = lastPrice ?? close;
          result.close = close;
          result.afterClose = null;
          result.hasAfterMarketData = false;
          // Only set previousClose if prevClose is valid, otherwise preserve the existing value from 3M data
          if (prevClose != null) {
            result.previousClose = prevClose;
          }
          result.previousAfterClose = prevLastPrice;
          break;

        case MarketStateInstrumentSnapshot.IS_MarketClosed:
          result.lastPrice = lastPrice ?? close;
          result.close = close;
          result.afterClose = lastPrice;
          result.hasAfterMarketData = !!(close && lastPrice && close !== lastPrice);
          // Only set previousClose if prevClose is valid, otherwise preserve the existing value from 3M data
          if (prevClose != null) {
            result.previousClose = prevClose;
          }
          result.previousAfterClose = prevLastPrice;
          break;

        case MarketStateInstrumentSnapshot.IS_MarketClosedNextDay:
          result.lastPrice = prevLastPrice ?? prevClose;
          result.close = prevClose;
          result.afterClose = prevLastPrice;
          result.hasAfterMarketData = !!(prevLastPrice && lastPrice && prevLastPrice !== lastPrice);
          // NOTE: Instrument Snapshot is lacking this data - previousClose, previousAfterClose
          break;

        default:
          console.error(`[market-data/priceSource.helpers] parseSymbolPriceSourceData - could not use Instrument Snapshot - unknown market state '${marketStateInstrumentSnapshot}'`);
          break;
      }

      const snapshotLineClose = getLastRegularSessionClose(
        marketStateInstrumentSnapshot,
        newData as InstrumentSnapshotData,
      );
      if (snapshotLineClose != null) {
        result.lineClose = snapshotLineClose;
      }
      break;
    }

    case 'chart-data': {
      const { data, bucket } = newData as OHLCVDataForPriceCalculation;
      const len = data?.length;
      const lastItem = data?.[len - 1];
      const mtz = getMomentTzLocal();
      const nowMoment = mtz().tz(timeZone);
      const nowAsDateStr = nowMoment.format(MARKET_DATA_DATE);
      const hasDataForToday = isDateEqual(nowAsDateStr, getOHLCVItemDateAndTime(lastItem));
      switch (bucket) {
        case GWMarketDataSummaryBucket.OneDay: {
          result.hasDataForToday = hasDataForToday;
          break;
        }
        case GWMarketDataSummaryBucket.ThreeMonths: {
          const lastButOneItem = data?.[len - 2];
          const lastButTwoItem = data?.[len - 3];
          // previousClose is not available from the Instrument Snapshot before the close,
          // therefore taken from 3M1D MarketDataSummary
          let previousClose: NullableNumber;
          switch (marketStateInstrumentSnapshot) {
            case MarketStateInstrumentSnapshot.IS_PreMarket:
              previousClose = hasDataForToday ? lastButTwoItem?.close : lastButOneItem?.close;
              result.previousClose = previousClose;
              break;
            case MarketStateInstrumentSnapshot.IS_RegularHours:
              previousClose = hasDataForToday ? lastButTwoItem?.close : lastButOneItem?.close;
              result.previousClose = previousClose;
              break;
            case MarketStateInstrumentSnapshot.IS_AfterMarket:
              previousClose = lastButOneItem?.close;
              result.previousClose = previousClose;
              break;
            case MarketStateInstrumentSnapshot.IS_MarketClosedNextDay:
              previousClose = lastButOneItem?.close;
              result.previousClose = previousClose;
              break;
            default:
              break;
          }
          break;
        }

        default:
          console.error(`[market-data/priceSource.helpers] parseSymbolPriceSourceData - unknown chart data bucket '${bucket}'`);
          break;
      }
      break;
    }

    default:
      console.error(`[market-data/priceSource.helpers] parseSymbolPriceSourceData - unknown inputType = '${inputType}'`);
      break;
  }

  return result;
}
